// src/pages/TicketLookup/components/QrScannerModal.tsx
// Modal quét mã QR bằng camera thiết bị, cho phép người dùng tra cứu vé khác ngay trong
// trang tra cứu mà không cần mở lại link mới.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Input, FormFeedback } from 'reactstrap';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';

const SCANNER_ELEMENT_ID = 'ticket-lookup-qr-scanner';

interface QrScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScanned: (decodedText: string) => void;
}

type ScannerStatus = 'starting' | 'scanning' | 'error';

const QrScannerModal: React.FC<QrScannerModalProps> = ({ isOpen, onClose, onScanned }) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  // Promise của lần start gần nhất — dùng để KHÔNG gọi stop() khi camera còn đang khởi động dở
  // (nguyên nhân lỗi "Cannot stop, scanner is not running or paused." khi mở rồi đóng modal nhanh).
  const startPromiseRef = useRef<Promise<void> | null>(null);
  const [status, setStatus] = useState<ScannerStatus>('starting');
  const [errorMessage, setErrorMessage] = useState('');
  const [manualLink, setManualLink] = useState('');
  const [manualError, setManualError] = useState('');

  // Chỉ khởi động camera SAU KHI Modal đã render/hiện xong (onOpened) — tránh race condition
  // gắn Html5Qrcode vào element chưa kịp có trong DOM khi Modal còn đang chạy animation mở.
  const startScanner = useCallback(() => {
    setStatus('starting');
    setErrorMessage('');
    setManualLink('');
    setManualError('');

    const html5QrCode = new Html5Qrcode(SCANNER_ELEMENT_ID);
    scannerRef.current = html5QrCode;

    startPromiseRef.current = html5QrCode
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          onScanned(decodedText);
        },
        () => {
          // Callback lỗi decode từng frame — bỏ qua, đây là tín hiệu bình thường khi chưa thấy mã QR nào
        }
      )
      .then(() => {
        if (scannerRef.current === html5QrCode) setStatus('scanning');
      })
      .catch((err) => {
        if (scannerRef.current !== html5QrCode) return;
        setStatus('error');
        const message = String(err?.message || err || '');
        if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('notallowed')) {
          setErrorMessage('Trình duyệt chưa được cấp quyền dùng camera. Vui lòng cho phép truy cập camera rồi thử lại.');
        } else if (message.toLowerCase().includes('notfound')) {
          setErrorMessage('Không tìm thấy camera trên thiết bị này.');
        } else {
          setErrorMessage('Không thể khởi động camera. Bạn có thể dán link vé bên dưới để tra cứu thủ công.');
        }
      });
  }, [onScanned]);

  const stopScanner = useCallback(() => {
    const instance = scannerRef.current;
    const startPromise = startPromiseRef.current;
    scannerRef.current = null;
    startPromiseRef.current = null;
    if (!instance) return;

    // Chỉ gọi stop() khi máy quét THỰC SỰ đang chạy hoặc đang tạm dừng — nếu không, html5-qrcode
    // ném thẳng (đồng bộ) "Cannot stop, scanner is not running or paused.". clear() để dọn DOM.
    const releaseCamera = () => {
      let state: Html5QrcodeScannerState = Html5QrcodeScannerState.UNKNOWN;
      try {
        state = instance.getState();
      } catch {
        state = Html5QrcodeScannerState.UNKNOWN;
      }

      const isRunning =
        state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED;

      if (!isRunning) {
        try {
          instance.clear();
        } catch {
          /* chưa render gì thì thôi */
        }
        return;
      }

      // Bọc trong Promise.resolve().then để lỗi ném đồng bộ (nếu có) cũng thành rejected promise
      // và bị .catch nuốt, không nổ ra ngoài (uncaught trong React commit phase).
      Promise.resolve()
        .then(() => instance.stop())
        .then(() => instance.clear())
        .catch(() => {
          /* camera có thể đã tự dừng — bỏ qua */
        });
    };

    // Nếu lần start còn đang chạy dở, đợi nó xong (thành công HAY thất bại) rồi mới đụng tới stop.
    if (startPromise) {
      startPromise.then(releaseCamera, releaseCamera);
    } else {
      releaseCamera();
    }
  }, []);

  // Đảm bảo camera luôn được giải phóng nếu component unmount đột ngột khi đang mở
  useEffect(() => stopScanner, [stopScanner]);

  const handleManualSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!manualLink.trim()) {
      setManualError('Vui lòng dán link tra cứu in trên vé');
      return;
    }
    setManualError('');
    onScanned(manualLink.trim());
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      onOpened={startScanner}
      onClosed={stopScanner}
      centered
      contentClassName="qr-scanner-modal"
    >
      <ModalHeader toggle={onClose}>
        <i className="ri-qr-scan-2-line align-bottom me-2"></i>
        Quét mã QR trên vé
      </ModalHeader>
      <ModalBody>
        <div className="qr-scanner-viewport">
          <div id={SCANNER_ELEMENT_ID} className="qr-scanner-region" />
          {status === 'starting' && (
            <div className="qr-scanner-overlay">
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Đang mở camera...</span>
              </div>
              <p className="mt-2 mb-0 text-light">Đang mở camera...</p>
            </div>
          )}
        </div>

        {status === 'scanning' && (
          <p className="text-muted text-center small mt-3 mb-0">
            Đưa mã QR trên vé vào khung hình để quét
          </p>
        )}

        {status === 'error' && (
          <div className="mt-3">
            <div className="alert alert-warning py-2 mb-3">
              <i className="ri-error-warning-line me-1"></i>
              {errorMessage}
            </div>
            <form onSubmit={handleManualSubmit}>
              <Input
                type="text"
                placeholder="Dán link tra cứu in trên vé tại đây"
                value={manualLink}
                onChange={(e) => setManualLink(e.target.value)}
                invalid={!!manualError}
              />
              {manualError && <FormFeedback>{manualError}</FormFeedback>}
              <Button color="primary" size="sm" type="submit" className="w-100 mt-2">
                Tra cứu
              </Button>
            </form>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default QrScannerModal;
